import { forwardRef } from 'react';
import type { Ref, PropsWithoutRef, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import {
  Text,
  Flex,
  Icon,
  defaultThemeProp,
  Sentiment,
  Button,
  useElement,
  InfoDialog,
  FieldValueList,
  useI18n,
  registerIcon,
  MenuButton
} from '@pega/cosmos-react-core';
import type {
  ForwardRefForwardPropsComponent,
  NoChildrenProp,
  SentimentProps,
  FieldValueListItemProps,
  MenuItemProps
} from '@pega/cosmos-react-core';
import * as userCheckIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/user-check.icon';
import * as userQuestionMarkIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/user-question-mark.icon';

registerIcon(userCheckIcon, userQuestionMarkIcon);

export interface ChatHeaderProps extends NoChildrenProp {
  /** ref to the element */
  ref?: Ref<HTMLElement>;
  /** Title: Used to show channel || group name */
  title: string;
  /** Icon: Used to show the channel icons, pass the icon name from the cosmos library to render */
  icon?: string;
  /** Used to show customer name || handle */
  customer: string;
  /** Authentication status of the customer
   * @default false
   */
  authenticated?: boolean;
  /** Sentiment */
  sentiment?: SentimentProps;
  /** Contextual information */
  context?: {
    heading?: string;
    loading?: boolean;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    items: FieldValueListItemProps[];
  };
  /** Used to show frequent user actions to be performed */
  actions?: MenuItemProps[];
}

const StyledChatHeader = styled.header(
  ({
    theme: {
      base: {
        spacing,
        palette: { 'border-line': borderLine }
      }
    }
  }) => {
    return css`
      padding: ${spacing};
      border-block-end: 0.0625rem solid ${borderLine};
    `;
  }
);
StyledChatHeader.defaultProps = defaultThemeProp;

const StyledContextContainer = styled.div`
  min-height: 2rem;
`;

const StyledInfoDialog = styled(InfoDialog)`
  min-width: 50ch;
`;

const ChatHeader: ForwardRefForwardPropsComponent<ChatHeaderProps> = forwardRef(function ChatHeader(
  {
    title,
    actions = [],
    icon,
    customer,
    authenticated = false,
    sentiment,
    context,
    ...restProps
  }: PropsWithoutRef<ChatHeaderProps>,
  ref: ChatHeaderProps['ref']
) {
  const t = useI18n();
  const [target, setTarget] = useElement<HTMLButtonElement>(null);

  return (
    <Flex
      {...restProps}
      ref={ref}
      as={StyledChatHeader}
      container={{ alignItems: 'start' }}
      item={{
        shrink: 0
      }}
    >
      <Flex
        item={{
          grow: 1
        }}
        container={{
          gap: 0.5,
          alignItems: 'start',
          direction: 'column'
        }}
      >
        <Flex container={{ gap: 1, alignItems: 'center' }}>
          {icon && <Icon name={icon} />}
          <Text variant='h2'>{title}</Text>
        </Flex>
        <Flex container={{ gap: 1, alignItems: 'center' }}>
          <Icon
            name={authenticated ? 'user-check' : 'user-question-mark'}
            aria-label={authenticated ? t('authenticated') : t('not_authenticated')}
          />
          <Text variant='secondary'>{customer}</Text>
          {sentiment && <Sentiment {...sentiment} labelHidden />}
        </Flex>
      </Flex>

      <Flex
        item={{
          grow: 1
        }}
        container={{
          alignItems: 'center',
          justify: 'end'
        }}
      >
        {context && (
          <Button
            variant='simple'
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              context.onClick?.(e);
              setTarget(e.currentTarget);
            }}
          >
            {t('context')}
          </Button>
        )}

        <MenuButton
          text={t('actions')}
          iconOnly
          icon='more'
          variant='simple'
          menu={{ items: actions }}
        />
      </Flex>
      {target && context && (
        <StyledInfoDialog
          heading={context.heading ?? t('context_data')}
          target={target}
          onDismiss={() => setTarget(null)}
          placement='bottom-end'
          progress={context.loading}
        >
          <Flex as={StyledContextContainer} container>
            <FieldValueList fields={context.items} />
          </Flex>
        </StyledInfoDialog>
      )}
    </Flex>
  );
});

export default ChatHeader;
